import { TFunction } from 'i18next';
import * as Yup from 'yup';
import { getErrorIndex } from '../../../utils/getErrorIndex';

export const createBillingOrderFirstStepSchema = (t: TFunction<'translation', undefined>) => {
  return Yup.object().shape({
    organization: Yup.object()
      .nullable()
      .test('organization-required', t('Please select an organization'), function (value: any) {
        if (!value || !value._id) {
          return false;
        }
        return true;
      })
      .shape({
        _id: Yup.string().required(t('Organization ID is required')),
        name: Yup.string().required(t('Organization name is required')),
        country: Yup.object()
          .nullable()
          .shape({
            _id: Yup.string().when('$root.organization._id', {
              is: (id: any) => id,
              then: (schema) => schema.required(t('Country is missing')),
              otherwise: (schema) => schema.nullable(),
            }),
            name: Yup.string().when('$root.organization._id', {
              is: (id: any) => id,
              then: (schema) => schema.required(t('Country name is missing')),
              otherwise: (schema) => schema.nullable(),
            }),
            flag_image: Yup.string(),
          }),
        owner: Yup.object()
          .nullable()
          .shape({
            _id: Yup.string().when('$root.organization._id', {
              is: (id: any) => id,
              then: (schema) => schema.required(t('Owner is missing')),
              otherwise: (schema) => schema.nullable(),
            }),
            firstName: Yup.string().when('$root.organization._id', {
              is: (id: any) => id,
              then: (schema) => schema.required(t('Owner first name is missing')),
              otherwise: (schema) => schema.nullable(),
            }),
            lastName: Yup.string().when('$root.organization._id', {
              is: (id: any) => id,
              then: (schema) => schema.required(t('Owner last name is missing')),
              otherwise: (schema) => schema.nullable(),
            }),
            email: Yup.string()
              .email()
              .when('$root.organization._id', {
                is: (id: any) => id,
                then: (schema) => schema.required(t('Owner email is required')),
                otherwise: (schema) => schema.nullable(),
              }),
          }),
        segment: Yup.object()
          .nullable()
          .shape({
            _id: Yup.string().when('$root.organization._id', {
              is: (id: any) => id,
              then: (schema) => schema.required(t('Business segment is missing')),
              otherwise: (schema) => schema.nullable(),
            }),
            name: Yup.string().when('$root.organization._id', {
              is: (id: any) => id,
              then: (schema) => schema.required(t('Segment name is missing')),
              otherwise: (schema) => schema.nullable(),
            }),
          }),
      })
      .required(t('Please select an organization')),
    selectedMilestones: Yup.array()
      .of(
        Yup.object().shape({
          _id: Yup.string().required(t('Milestone ID is required')),
          orderItem: Yup.object()
            .nullable()
            .test('required', function (value) {
              if (!value) {
                const index = getErrorIndex(this.path);
                return this.createError({
                  message: t(`Milestone ${index}: Order item is required`.trim()),
                });
              }
              return true;
            })
            .shape({
              _id: Yup.string().when('orderItem', {
                is: (orderItem: any) => orderItem?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (!value) {
                      const index = getErrorIndex(this.path);
                      return this.createError({
                        message: t(`Milestone ${index}: Order item ID is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
              subscription: Yup.string().when('orderItem', {
                is: (orderItem: any) => orderItem?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (!value) {
                      const match = this.path.match(/\[(\d+)\]/);
                      const index = match ? parseInt(match[1], 10) + 1 : '';
                      return this.createError({
                        message: t(`Milestone ${index}: Subscription is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
              quantity: Yup.number().when('orderItem', {
                is: (orderItem: any) => orderItem?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (value === undefined || value === null) {
                      const index = getErrorIndex(this.path);
                      return this.createError({
                        message: t(`Milestone ${index}: Quantity is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
              orderItemType: Yup.string().nullable(),
              provisionDate: Yup.date().nullable(),
              renewalDateCommercial: Yup.date().test('required', function (value) {
                if (!value) {
                  const index = getErrorIndex(this.path);
                  return this.createError({
                    message: t(`Milestone ${index}: Renewal date is missing`.trim()),
                  });
                }
                return true;
              }),
            }),
          paymentDate: Yup.date().test('required', function (value) {
            if (!value) {
              const index = getErrorIndex(this.path);
              return this.createError({
                message: t(`Milestone ${index}: Payment date is required`.trim()),
              });
            }
            return true;
          }),
          milestonePeriod: Yup.number().test('required', function (value) {
            if (value === undefined || value === null) {
              const index = getErrorIndex(this.path);
              return this.createError({
                message: t(`Milestone ${index}: Milestone period is required`.trim()),
              });
            }
            return true;
          }),
          type: Yup.string().test('required', function (value) {
            if (!value) {
              const index = getErrorIndex(this.path);
              return this.createError({
                message: t(`Milestone ${index}: Milestone type is required`.trim()),
              });
            }
            return true;
          }),
          totalDiscount: Yup.number().min(0, t('Discount cannot be negative')),
          subtotal: Yup.number().test('required', function (value) {
            if (value === undefined || value === null) {
              const index = getErrorIndex(this.path);
              return this.createError({
                message: t(`Milestone ${index}: Subtotal is required`.trim()),
              });
            }
            return true;
          }),
          total: Yup.number()
            .test('required', function (value) {
              if (value === undefined || value === null) {
                const index = getErrorIndex(this.path);
                return this.createError({
                  message: t(`Milestone ${index}: Total is required`.trim()),
                });
              }
              return true;
            })
            .min(0, t('Total cannot be negative')),
          startPeriod: Yup.date().test('required', function (value) {
            if (!value) {
              const index = getErrorIndex(this.path);
              return this.createError({
                message: t(`Milestone ${index}: Start period is required`.trim()),
              });
            }
            return true;
          }),
          endPeriod: Yup.date().test('required', function (value) {
            if (!value) {
              const index = getErrorIndex(this.path);
              return this.createError({
                message: t(`Milestone ${index}: End period is required`.trim()),
              });
            }
            return true;
          }),
          status: Yup.string().test('required', function (value) {
            if (!value) {
              const index = getErrorIndex(this.path);
              return this.createError({
                message: t(`Milestone ${index}: Milestone status is required`.trim()),
              });
            }
            return true;
          }),
          product: Yup.object()
            .nullable()
            .test('required', function (value) {
              if (!value) {
                const index = getErrorIndex(this.path);
                return this.createError({
                  message: t(`Milestone ${index}: Product is required`.trim()),
                });
              }
              return true;
            })
            .shape({
              _id: Yup.string().test('required', function (value) {
                if (!value) {
                  const index = getErrorIndex(this.path);
                  return this.createError({
                    message: t(`Milestone ${index}: Product ID is missing`.trim()),
                  });
                }
                return true;
              }),
              name: Yup.string().when('product', {
                is: (product: any) => product?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (!value) {
                      const index = getErrorIndex(this.path);
                      return this.createError({
                        message: t(`Milestone ${index}: Product name is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
              label: Yup.string().nullable(),
              isNiif15: Yup.boolean().when('product', {
                is: (product: any) => product?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (value === undefined || value === null) {
                      const index = getErrorIndex(this.path);
                      return this.createError({
                        message: t(`Milestone ${index}: NIIF15 indicator is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
            }),
          sku: Yup.object()
            .nullable()
            .test('required', function (value) {
              if (!value) {
                const index = getErrorIndex(this.path);
                return this.createError({
                  message: t(`Milestone ${index}: SKU is required`.trim()),
                });
              }
              return true;
            })
            .shape({
              _id: Yup.string().test('required', function (value) {
                if (!value) {
                  const index = getErrorIndex(this.path);
                  return this.createError({
                    message: t(`Milestone ${index}: SKU ID is missing`.trim()),
                  });
                }
                return true;
              }),
              name: Yup.string().when('sku', {
                is: (sku: any) => sku?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (!value) {
                      const index = getErrorIndex(this.path);
                      return this.createError({
                        message: t(`Milestone ${index}: SKU name is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
              label: Yup.string().nullable(),
            }),
          plan: Yup.object()
            .nullable()
            .test('required', function (value) {
              if (!value) {
                const index = getErrorIndex(this.path);
                return this.createError({
                  message: t(`Milestone ${index}: Plan is required`.trim()),
                });
              }
              return true;
            })
            .shape({
              _id: Yup.string().test('required', function (value) {
                if (!value) {
                  const index = getErrorIndex(this.path);
                  return this.createError({
                    message: t(`Milestone ${index}: Plan ID is missing`.trim()),
                  });
                }
                return true;
              }),
              name: Yup.string().when('plan', {
                is: (plan: any) => plan?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (!value) {
                      const index = getErrorIndex(this.path);

                      return this.createError({
                        message: t(`Milestone ${index}: Plan name is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
              code: Yup.string().when('plan', {
                is: (plan: any) => plan?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (!value) {
                      const index = getErrorIndex(this.path);
                      return this.createError({
                        message: t(`Milestone ${index}: Plan code is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
              label: Yup.string().nullable(),
              catalogName: Yup.string().when('plan', {
                is: (plan: any) => plan?._id,
                then: (schema) =>
                  schema.test('required', function (value) {
                    if (!value) {
                      const index = getErrorIndex(this.path);
                      return this.createError({
                        message: t(`Milestone ${index}: Catalog name is missing`.trim()),
                      });
                    }
                    return true;
                  }),
                otherwise: (schema) => schema.nullable(),
              }),
            }),
        })
      )
      .min(1, t('At least one milestone must be selected'))
      .required(t('Milestones are required')),
    onlySave: Yup.boolean().default(false),
  });
};
